import { useState, useEffect } from 'react'
import type { ModelData, ConfigurationState, PricingData } from '../types'
import { formatPrice, getCurrentDate, getCurrentDateTime, copyToClipboard } from '../utils/helpers'
import { isFusionAvailable, isFusionCornerAvailable, isAkuPlusAvailable, getThermalHangersData, generateGlassOptions } from '../utils/optionGenerator'
import { generateDirectProductCode, MODEL_CODES } from '../utils/directProductCode'
import { getModelGroupFromModel } from '../utils/modelGroupHelper'
import { modelsData } from '../data/models'
import { auth, functions } from '../config/firebase'
import { httpsCallable } from 'firebase/functions'
import SavedConfigurationsService from '../services/SavedConfigurationsService'
import { getNemoControllerFullLabel } from '../data/controllerData'
import pdfMake from 'pdfmake/build/pdfmake'
// @ts-ignore - vfs_fonts nie ma pełnych typów
import pdfFonts from 'pdfmake/build/vfs_fonts'
import * as pdfjsLib from 'pdfjs-dist'

// Konfiguracja pdf.js - użyj lokalnego workera
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

// Konfiguracja fontów dla pdfmake
// @ts-ignore
if (pdfFonts?.pdfMake?.vfs) {
  // @ts-ignore
  pdfMake.vfs = pdfFonts.pdfMake.vfs
}

// Funkcja generująca definicję dokumentu PDF dla pdfmake
const generatePdfDefinition = (
  summaryData: any,
  model: ModelData | null,
  config: ConfigurationState,
  _pricing: PricingData,
  _productCode: string,
  _clientNotes: string,
  _distributorInfo: string,
  logoDataUrl: string,
  priceMode: 'netto' | 'vat8' | 'vat23'
): any => {
  const modelId = (model?.id || '').toLowerCase()
  const isNemoModel = modelId.startsWith('nemo') || (model?.name ? model.name.toLowerCase().includes('nemo') : false)

  return {
    pageSize: 'A4' as const,
    pageMargins: [40, 70, 40, 40] as [number, number, number, number],
    
    // Header z logo
    header: function(currentPage: number, _pageCount: number) {
      if (currentPage === 1) {
        const headerStack: any[] = [
          // Granatowe tło - renderuje się zawsze
          {
            canvas: [
              {
                type: 'rect',
                x: 0,
                y: 0,
                w: 110,
                h: 48,
                color: '#2B3E50'
              }
            ],
            absolutePosition: { x: 470, y: 12 },
            width: 110,
            height: 48
          }
        ];
        
        // Dodaj logo tylko jeśli się załadowało
        if (logoDataUrl) {
          headerStack.push({
            image: logoDataUrl,
            absolutePosition: { x: 487, y: 23 },
            fit: [75, 24]
          });
        }
        
        return { stack: headerStack };
      }
      return {};
    },
    
    // Footer z informacją o wygenerowaniu
    footer: function(_currentPage: number, _pageCount: number) {
      return {
        text: `Dokument wygenerowany automatycznie - ${getCurrentDateTime()}`,
        alignment: 'center' as const,
        fontSize: 10,
        color: '#666666',
        margin: [0, 10, 0, 10] as [number, number, number, number]
      };
    },
    
    // Definicja stylów
    styles: {
      header: {
        fontSize: 20,
        color: '#C4A574',
        alignment: 'left' as const,
        margin: [0, 0, 0, 10] as [number, number, number, number]
      },
      subheader: {
        fontSize: 12,
        bold: true,
        color: '#C4A574',
        margin: [0, 10, 0, 5] as [number, number, number, number]
      },
      normal: {
        fontSize: 12,
        margin: [0, 2, 0, 2] as [number, number, number, number]
      },
      tableHeader: {
        fontSize: 12,
        bold: true,
        fillColor: '#E5D4B8',
        color: '#000000'
      },
      small: {
        fontSize: 10,
        color: '#666666'
      },
      listItem: {
        fontSize: 12,
        margin: [0, 2, 0, 2] as [number, number, number, number]
      }
    },
    
    // Domyślny styl z UTF-8
    defaultStyle: {
      font: 'Roboto'
    },
    
    // Zawartość dokumentu
    content: [
      // Nagłówek główny
      { 
        text: 'KONFIGURACJA PRODUKTU UNICO', 
        style: 'header',
        margin: [0, 0, 0, 10] as [number, number, number, number]
      },
      
      // Dystrybutor (jeśli jest) - zaraz pod tytułem
      ...(_distributorInfo ? [
        {
          text: [
            { text: 'Dystrybutor: ', bold: true, color: '#C4A574' },
            { text: _distributorInfo }
          ],
          style: 'normal',
          margin: [0, 10, 0, 20] as [number, number, number, number]
        }
      ] : [
        {
          text: '',
          margin: [0, 10, 0, 10] as [number, number, number, number]
        }
      ]),
      
      // Dane podstawowe
      {
        text: [
          { text: 'Model: ', bold: true },
          summaryData.model
        ],
        style: 'normal'
      },
      {
        text: [
          { text: 'Moc: ', bold: true },
          summaryData.power
        ],
        style: 'normal'
      },
      {
        text: [
          { text: 'Wymiary fasady: ', bold: true },
          summaryData.specs.glassSize
        ],
        style: 'normal'
      },
      ...(summaryData.specs.efficiency ? [{
        text: [
          { text: 'Sprawność: ', bold: true },
          '-'
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.energyClass ? [{
        text: [
          { text: 'Klasa energetyczna: ', bold: true },
          summaryData.specs.energyClass
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.waterHeaterPower ? [{
        text: [
          { text: 'Moc wymiennika wodnego: ', bold: true },
          summaryData.specs.waterHeaterPower
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.waterCapacity ? [{
        text: [
          { text: 'Pojemność wymiennika: ', bold: true },
          summaryData.specs.waterCapacity
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.insertSize ? [{
        text: [
          { text: 'Wymiary wkładu: ', bold: true },
          summaryData.specs.insertSize
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.fireplaceSize ? [{
        text: [
          { text: 'Wymiary paleniska: ', bold: true },
          summaryData.specs.fireplaceSize
        ],
        style: 'normal'
      }] : []),
      ...(summaryData.specs.maxLogLength ? [{
        text: [
          { text: 'Maks. długość polan: ', bold: true },
          summaryData.specs.maxLogLength
        ],
        style: 'normal'
      }] : []),
      {
        text: [
          { text: 'Waga: ', bold: true },
          summaryData.specs.weight
        ],
        style: 'normal',
        margin: [0, 2, 0, 2] as [number, number, number, number]
      },
      
      // Kod produktu
      {
        text: [
          { text: 'Kod konfiguracji: ', bold: true },
          { text: _productCode, color: '#C4A574' }
        ],
        style: 'normal',
        margin: [0, 2, 0, 20] as [number, number, number, number]
      },
      
      // Nagłówek sekcji szczegółów
      {
        text: 'Szczegóły konfiguracji:',
        style: 'subheader'
      },
      
      // Tabela szczegółów konfiguracji
      {
        table: {
          headerRows: 1,
          widths: ['30%', '50%', '20%'],
          body: [
            // Nagłówek tabeli
            [
              { text: 'Opcja', style: 'tableHeader' },
              { text: 'Opis', style: 'tableHeader' },
              { text: 'Cena netto', style: 'tableHeader' }
            ],
            // Dane w tabeli - będą dodawane dynamicznie
            ...(() => {
              const rows: any[] = [];
              
              // Model bazowy
              const modelPrice = (() => {
                // For corner models, show cheapest glass price; for others, show base price minus glass surcharge
                if (model?.glassType === 'narozna') {
                  const variant = config?.variant === 'lift' ? 'lift' : 'standard';
                  const variantPrices = model.basePrices?.[variant] || {};
                  const cheapestPrice = Math.min(...Object.values(variantPrices).filter(price => typeof price === 'number'));
                  return cheapestPrice;
                } else {
                  return _pricing.basePrice - _pricing.breakdown.glassPrice;
                }
              })();
              rows.push([
                { text: 'Model', style: 'normal' },
                { text: summaryData.model, style: 'normal' },
                { text: formatPrice(modelPrice) + ' zł', style: 'normal' }
              ]);
              
              // Wariant otwierania
              rows.push([
                { text: 'Wariant otwierania', style: 'normal' },
                { text: summaryData.variant, style: 'normal' },
                { text: '-', style: 'normal' }
              ]);
              
              // Szyba
              const glassPrice = _pricing.breakdown.glassPrice > 0 ? '+' + formatPrice(_pricing.breakdown.glassPrice) + ' zł' : '-';
              rows.push([
                { text: 'Szyba', style: 'normal' },
                { text: summaryData.glassType, style: 'normal' },
                { text: glassPrice, style: 'normal' }
              ]);
              
              // Side glass for corner Dragon/Nemo models (both standard and LIFT)
              if ((config.variant === 'lift' || config.variant === 'standard') && model?.glassType === 'narozna' && 
                  (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) &&
                  (config.sideGlass === 'left' || config.sideGlass === 'right')) {
                const sideGlassText = config.sideGlass === 'left' ? 'Lewa' : 'Prawa';
                rows.push([
                  { text: 'Strona wkładu', style: 'normal' },
                  { text: sideGlassText, style: 'normal' },
                  { text: '-', style: 'normal' }
                ]);
              }
              
              // Ceramika
              rows.push([
                { text: 'Ceramika (wyłożenie)', style: 'normal' },
                { text: summaryData.ceramic, style: 'normal' },
                { text: '-', style: 'normal' }
              ]);
              
              // Klamka
              rows.push([
                { text: 'Klamka', style: 'normal' },
                { text: summaryData.handle, style: 'normal' },
                { text: '-', style: 'normal' }
              ]);
              
              // Strona klamki
              if (model?.glassType === 'vis-a-vis') {
                // vis-a-vis models - show front first (standard or lift), then back
                const frontVariant = config['front-variant']
                const backVariant = config['back-variant']
                const frontHandleSide = config['front-handleSide']
                const backHandleSide = config['back-handleSide']

                
                // Show front first (standard or lift depending on variant)
                if (frontVariant === 'standard' && frontHandleSide) {
                  rows.push([
                    { text: 'Strona klamki (Przód)', style: 'normal' },
                    { text: frontHandleSide === 'left' ? 'Lewa' : 'Prawa', style: 'normal' },
                    { text: '-', style: 'normal' }
                  ]);

                }
                
                // Then show back (standard or lift depending on variant)
                if (backVariant === 'standard' && backHandleSide) {
                  rows.push([
                    { text: 'Strona klamki (Tył)', style: 'normal' },
                    { text: backHandleSide === 'left' ? 'Lewa' : 'Prawa', style: 'normal' },
                    { text: '-', style: 'normal' }
                  ]);

                }
              } else {
                // Standard models
                const isCornerModel = model?.glassType === 'narozna';
                const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
                const hasAutoHandleSide = config.variant === 'standard' && isCornerModel && isDragonOrNemo && config.sideGlass;
                
                if (config.variant === 'standard' && (config.handleSide || hasAutoHandleSide)) {
                  let handleSideText: string;
                  
                  if (hasAutoHandleSide) {
                    // For standard corner models with sideGlass, handle side is auto-determined
                    const autoHandleSide = config.sideGlass === 'right' ? 'left' : 'right';
                    handleSideText = autoHandleSide === 'left' ? 'Lewa' : 'Prawa';
                  } else {
                    // Regular standard models with manual handle side selection
                    handleSideText = config.handleSide === 'left' ? 'Lewa' : 'Prawa';
                  }
                  
                  rows.push([
                    { text: 'Strona klamki', style: 'normal' },
                    { text: handleSideText, style: 'normal' },
                    { text: '-', style: 'normal' }
                  ]);

                }
              }
              
              // Blenda
              const bezelPrice = _pricing.breakdown.bezelPrice > 0 ? '+' + formatPrice(_pricing.breakdown.bezelPrice) + ' zł' : '-';
              rows.push([
                { text: 'Blenda', style: 'normal' },
                { text: summaryData.bezel, style: 'normal' },
                { text: bezelPrice, style: 'normal' }
              ]);
              
              // FUSION (jeśli dostępne)
              if (summaryData.fusion) {
                const fusionPrice = _pricing.breakdown.fusionPrice > 0 ? '+' + formatPrice(_pricing.breakdown.fusionPrice) + ' zł' : '-';
                rows.push([
                  { text: 'Zabudowa FUSION', style: 'normal' },
                  { text: summaryData.fusion, style: 'normal' },
                  { text: fusionPrice, style: 'normal' }
                ]);
              }
              
              // FUSION CORNER (jeśli dostępne)
              if (summaryData.fusionCorner) {
                const fusionCornerPrice = _pricing.breakdown.fusionCornerPrice > 0 ? '+' + formatPrice(_pricing.breakdown.fusionCornerPrice) + ' zł' : '-';
                rows.push([
                  { text: 'Zabudowa FUSION CORNER', style: 'normal' },
                  { text: summaryData.fusionCorner, style: 'normal' },
                  { text: fusionCornerPrice, style: 'normal' }
                ]);
              }
              
              // AKU+ (jeśli dostępne)
              if (summaryData.akuPlus === 'aku-plus') {
                const akuPlusPrice = _pricing.breakdown.akuPlusPrice > 0 ? '+' + formatPrice(_pricing.breakdown.akuPlusPrice) + ' zł' : '-';
                rows.push([
                  { text: 'AKU+', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: akuPlusPrice, style: 'normal' }
                ]);
              }
              
              // NEMO zabezpieczenie termiczne (jeśli dostępne)
              if (config.nemoThermalProtection === 'with-protection') {
                const thermalPrice = _pricing.breakdown.nemoThermalProtectionPrice && _pricing.breakdown.nemoThermalProtectionPrice > 0 
                  ? '+' + formatPrice(_pricing.breakdown.nemoThermalProtectionPrice) + ' zł' 
                  : '-';
                rows.push([
                  { text: 'Zabezpieczenie termiczne chłodnicy', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: thermalPrice, style: 'normal' }
                ]);
              }
              
              // AKU BOX (jeśli dostępne)
              if (config.akuBox && config.akuBox !== 'none') {
                const akuBoxPrice = _pricing.breakdown.akuBoxPrice > 0 ? '+' + formatPrice(_pricing.breakdown.akuBoxPrice) + ' zł' : '-';
                rows.push([
                  { text: 'Wymiennik akumulacyjny AKUBOX', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: akuBoxPrice, style: 'normal' }
                ]);
              }
              
              // EASY 700 (jeśli dostępne)
              if (!isNemoModel && config.easy700 && config.easy700 !== 'none') {
                const easy700Price = _pricing.breakdown.easy700Price > 0 ? '+' + formatPrice(_pricing.breakdown.easy700Price) + ' zł' : '-';
                rows.push([
                  { text: 'Wymiennik stalowo-szamotowy Easy 700', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: easy700Price, style: 'normal' }
                ]);
              }
              
              // CMA N (jeśli dostępne)
              if (config.cmaN && config.cmaN !== 'none') {
                const cmaNPrice = _pricing.breakdown.cmaNPrice > 0 ? '+' + formatPrice(_pricing.breakdown.cmaNPrice) + ' zł' : '-';
                rows.push([
                  { text: 'Moduł akumulacyjny CMA N (333 kg, 112x56x35 mm)', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: cmaNPrice, style: 'normal' }
                ]);
              }
              
              // CMA S (jeśli dostępne)
              if (config.cmaS && config.cmaS !== 'none') {
                const cmaSPrice = _pricing.breakdown.cmaSPrice > 0 ? '+' + formatPrice(_pricing.breakdown.cmaSPrice) + ' zł' : '-';
                rows.push([
                  { text: 'Moduł akumulacyjny CMA S (338 kg, 119x56x28 mm)', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: cmaSPrice, style: 'normal' }
                ]);
              }
              
              // MODUŁY TERMOAKUMULACYJNE (jeśli dostępne)
              if (config.thermalModules && config.thermalModules !== 'none') {
                const modulesPrice = _pricing.breakdown.thermalModulesPrice > 0 ? '+' + formatPrice(_pricing.breakdown.thermalModulesPrice) + ' zł' : '-';
                const count = config.thermalModulesCount || 1;
                const fullLabel = (model?.labels?.thermalModules?.[config.thermalModules] || config.thermalModules).replace(/^Dodaj\s+/i, '');
                const modulesDesc = `${count} szt. (${fullLabel})`;
                rows.push([
                  { text: 'MODUŁY TERMOAKUMULACYJNE', style: 'normal' },
                  { text: modulesDesc, style: 'normal' },
                  { text: modulesPrice, style: 'normal' }
                ]);
              }
              
              // Zawiesia termoakumulacyjne (jeśli dostępne)
              if (config.thermalHangers === 'yes') {
                const hangersPrice = _pricing.breakdown.thermalHangersPrice > 0 ? '+' + formatPrice(_pricing.breakdown.thermalHangersPrice) + ' zł' : '-';
                rows.push([
                  { text: 'ZAWIESIA TERMOAKUMULACYJNE', style: 'normal' },
                  { text: 'Komplet z uchwytami', style: 'normal' },
                  { text: hangersPrice, style: 'normal' }
                ]);
              }
              
              // STEROWNIKI (jeśli wybrany)
              if (config.controller && config.controller !== 'none') {
                const controllerPrice = _pricing.breakdown.controllerPrice > 0 ? '+' + formatPrice(_pricing.breakdown.controllerPrice) + ' zł' : '-';
                const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller;
                const isNemo = model?.id?.startsWith('nemo-');
                
                let displayLabel: string;
                if (isNemo) {
                  // Użyj funkcji pomocniczej dla NEMO
                  displayLabel = getNemoControllerFullLabel(
                    config.controller,
                    config.controllerColor || 'none',
                    config.controllerBuffor || false
                  );
                } else {
                  // Dragon logic
                  const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb';
                  displayLabel = controllerLabel;
                  if (requiresColor && config.controllerColor && config.controllerColor !== 'none') {
                    const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK';
                    displayLabel = controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel;
                  }
                }
                
                rows.push([
                  { text: 'STEROWNIK', style: 'normal' },
                  { text: displayLabel, style: 'normal' },
                  { text: controllerPrice, style: 'normal' }
                ]);
              }

              // CZUJNIK DRZWICZEK (jeśli wybrany)
              if (config.doorSensor === 'czujnik-drzwiczek') {
                const doorSensorPrice = _pricing.breakdown.doorSensorPrice > 0 ? '+' + formatPrice(_pricing.breakdown.doorSensorPrice) + ' zł' : '-';
                
                rows.push([
                  { text: 'Czujnik drzwiczek', style: 'normal' },
                  { text: 'Tak', style: 'normal' },
                  { text: doorSensorPrice, style: 'normal' }
                ]);
              }
              
              return rows;
            })()
          ]
        },
        layout: {
          hLineWidth: function (i: number, node: any) {
            if (i === 0) return 0;
            if (i === node.table.body.length) return 0.5;
            return 0.5;
          },
          vLineWidth: function () {
            return 0;
          },
          hLineColor: function () {
            return '#ddd';
          },
          paddingLeft: function () { return 8; },
          paddingRight: function () { return 8; },
          paddingTop: function () { return 4; },
          paddingBottom: function () { return 4; }
        }
      },
      
      // Sekcja RAZEM
      {
        table: {
          widths: ['*', '20%'],
          body: (() => {
            const rows: any[] = [];
            
            // Jeśli wybrano VAT (8% lub 23%), pokazujemy rozbicie
            if (priceMode === 'vat8' || priceMode === 'vat23') {
              const nettoPrice = _pricing.totalPrice;
              const vatRate = priceMode === 'vat8' ? 0.08 : 0.23;
              const vatAmount = Math.round(nettoPrice * vatRate);
              const totalPrice = nettoPrice + vatAmount;
              
              rows.push([
                { text: 'Cena netto', style: 'normal', border: [true, true, false, true] },
                { text: formatPrice(nettoPrice) + ' zł', style: 'normal', alignment: 'left' as const, border: [false, true, true, true] }
              ]);
              
              rows.push([
                { text: `VAT ${priceMode === 'vat8' ? '8%' : '23%'}`, style: 'normal', border: [true, true, false, true] },
                { text: formatPrice(vatAmount) + ' zł', style: 'normal', alignment: 'left' as const, border: [false, true, true, true] }
              ]);
              
              rows.push([
                { text: 'RAZEM', style: 'tableHeader', border: [false, false, false, false], fillColor: '#E5D4B8' },
                { text: formatPrice(totalPrice) + ' zł', style: 'tableHeader', alignment: 'left' as const, border: [false, false, false, false], fillColor: '#E5D4B8' }
              ]);
            } else {
              // Dla netto pokazujemy tylko jedną linię
              const nettoPrice = _pricing.totalPrice;
              rows.push([
                { text: 'RAZEM', style: 'tableHeader', border: [false, false, false, false], fillColor: '#E5D4B8' },
                { text: formatPrice(nettoPrice) + ' zł (netto)', style: 'tableHeader', alignment: 'left' as const, border: [false, false, false, false], fillColor: '#E5D4B8', noWrap: true }
              ]);
            }
            
            return rows;
          })()
        },
        layout: {
          hLineWidth: function (_i: number, _node: any) {
            return 0.5;
          },
          vLineWidth: function (_i: number) {
            return 0.5;
          },
          hLineColor: function () {
            return '#E5D4B8';
          },
          vLineColor: function () {
            return '#E5D4B8';
          },
          paddingLeft: function () { return 8; },
          paddingRight: function () { return 8; },
          paddingTop: function () { return 6; },
          paddingBottom: function () { return 6; }
        },
        margin: [0, 20, 0, 15] as [number, number, number, number]
      },
      
      // Uwagi - zawsze pokazujemy tę sekcję
      {
        stack: [
          {
            text: 'Uwagi:',
            style: 'subheader'
          },
          {
            text: _clientNotes || 'Brak',
            style: 'normal',
            margin: [0, 0, 0, 0] as [number, number, number, number]
          }
        ],
        unbreakable: true,
        margin: [0, 0, 0, 15] as [number, number, number, number]
      },
      
      // Uwagi i informacje dodatkowe - jako jedna sekcja z pageBreak avoid
      {
        stack: [
          {
            text: 'Uwagi i informacje dodatkowe:',
            style: 'subheader'
          },
          {
            ul: [
              { text: 'Cena nie zawiera transportu i montażu', style: 'listItem' },
              { text: 'Dostępność i termin wysyłki do ustalenia z działem sprzedaży', style: 'listItem' }
            ],
            margin: [0, 0, 0, 0] as [number, number, number, number]
          }
        ],
        unbreakable: true,
        margin: [0, 0, 0, 0] as [number, number, number, number]
      }
    ]
  }
}

interface SummaryModalProps {
  model: ModelData | null
  config: ConfigurationState
  pricing: PricingData
  onClose: () => void
  priceMode: 'netto' | 'vat8' | 'vat23'
  isAdminLoggedIn?: boolean
  isClientLoggedIn?: boolean
}

const SummaryModal = ({ model, config, pricing, onClose, priceMode, isAdminLoggedIn, isClientLoggedIn }: SummaryModalProps) => {
  const [clientNotes, setClientNotes] = useState('')
  const [distributorInfo, setDistributorInfo] = useState('')
  const [isDataReady, setIsDataReady] = useState(false)
  const [isPdfLibraryReady, setIsPdfLibraryReady] = useState(false)
  const [logoDataUrl, setLogoDataUrl] = useState<string>('')
  
  // Track authentication state dynamically
  const [user, setUser] = useState(auth.currentUser)

  // Calculate price with VAT based on selected mode
  const calculatePriceWithVAT = (basePrice: number): number => {
    const vatMultiplier = priceMode === 'vat8' ? 1.08 : priceMode === 'vat23' ? 1.23 : 1;
    return Math.round(basePrice * vatMultiplier);
  };

  // Get VAT label for display
  const getVATLabel = (): string => {
    return priceMode === 'vat8' ? ' (8% VAT)' : priceMode === 'vat23' ? ' (23% VAT)' : ' (netto)';
  };
  
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [configName, setConfigName] = useState('')
  const [saving, setSaving] = useState(false)

  const modelId = (model?.id || '').toLowerCase()
  const isNemoModel = modelId.startsWith('nemo') || (model?.name ? model.name.toLowerCase().includes('nemo') : false)

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser)
    })
    return () => unsubscribe()
  }, [])

  const handleSaveConfiguration = async () => {
    if (!user) {
      alert('Musisz być zalogowany, aby zapisać konfigurację')
      return
    }

    if (!configName.trim()) {
      alert('Podaj nazwę dla konfiguracji')
      return
    }

    // Model safety check
    if (!model) {
      alert('Błąd: brak danych modelu')
      return
    }

    setSaving(true)
    try {
      // Determine model key and group based on model data
      const modelKey = model.id || model.name.toLowerCase().replace(/\s+/g, '-')
      const modelGroup = getModelGroupFromModel(model)

      console.log('Saving configuration with:', {
        modelKey,
        modelGroup,
        modelId: model.id,
        modelName: model.name
      })

      // Generate product code
      const productCode = generateDirectProductCode(modelGroup, modelKey, config)

      await SavedConfigurationsService.saveConfiguration(
        user.uid,
        configName.trim(),
        model.name,
        modelKey,
        modelGroup,
        productCode,
        config,
        pricing,
        clientNotes
      )

      alert('Konfiguracja została zapisana!')
      setShowSaveDialog(false)
      setConfigName('')
    } catch (error: any) {
      console.error('Error saving configuration:', error)
      console.error('Error details:', {
        errorCode: error?.code,
        errorMessage: error?.message,
        userId: user?.uid,
        modelName: model?.name
      })
      
      let errorMessage = 'Wystąpił błąd podczas zapisywania konfiguracji'
      if (error?.code === 'permission-denied') {
        errorMessage = 'Brak uprawnień do zapisywania. Sprawdź czy jesteś zalogowany.'
      } else if (error?.code === 'unauthenticated') {
        errorMessage = 'Musisz być zalogowany, aby zapisać konfigurację.'
      }
      
      alert(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  // Safety checks for required data
  if (!model || !config || !pricing) return null

  // Load logo as data URL for PDF
  useEffect(() => {
    const loadLogo = () => {
      // Używamy zawsze ścieżki z root domeny
      const logoPath = '/images/unico-logo.png'
      
      console.log('[PDF Logo] Window location:', window.location.pathname)
      console.log('[PDF Logo] Próba załadowania logo z:', logoPath)
      
      // Użyj bezpośrednio Image() zamiast fetch - tak samo jak reszta obrazków
      const img = new Image()
      
      img.onload = () => {
        console.log('[PDF Logo] Logo załadowane pomyślnie')
        const canvas = document.createElement('canvas')
        // Zwiększ rozdzielczość dla lepszej jakości w PDF
        const scale = 2
        canvas.width = img.width * scale
        canvas.height = img.height * scale
        
        const ctx = canvas.getContext('2d')
        if (ctx) {
          // Włącz antyaliasing dla gładszych krawędzi
          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          
          // Konwertuj do data URL z wysoką jakością
          const dataUrl = canvas.toDataURL('image/png', 1.0)
          setLogoDataUrl(dataUrl)
          console.log('[PDF Logo] Logo przekonwertowane do DataURL, długość:', dataUrl.length)
        }
      }
      
      img.onerror = (error) => {
        console.error('[PDF Logo] Nie udało się załadować logo:', error)
        console.error('[PDF Logo] Próbowana ścieżka:', logoPath)
        // Logo nie załadowało się, ale PDF i tak zostanie wygenerowany bez niego (z granatowym tłem)
      }
      
      // Ustaw src - to uruchomi ładowanie
      img.src = logoPath
    }
    
    loadLogo()
  }, [])

  // Use effect to ensure data is fully loaded before rendering main content
  useEffect(() => {
    // Small delay to ensure all data is properly loaded and hydrated
    const timer = setTimeout(() => {
      if (model?.name && config && pricing) {
        setIsDataReady(true)
      }
    }, 100) // 100ms delay should be enough to avoid race conditions

    return () => clearTimeout(timer)
  }, [model?.name, config, pricing])

  // Check for PDF library availability
  useEffect(() => {
    let retryCount = 0
    const maxRetries = 10
    
    const checkPdfLibrary = () => {
      if ((window as any).html2pdf) {
        setIsPdfLibraryReady(true)
      } else if (retryCount < maxRetries) {
        retryCount++
        // Retry after a short delay in case library is still loading
        setTimeout(checkPdfLibrary, 500)
      } else {
        console.warn('PDF library could not be loaded from CDN')
        // Library couldn't be loaded - user will see disabled button
      }
    }
    
    checkPdfLibrary()
  }, [])

  // Generate product code based on configuration using direct code system
  const generateProductCode = (): string => {
    // Safety check for model
    if (!model?.name) return 'MODEL-NOT-LOADED'
    
    // Find model group and key
    let modelGroup = ''
    let modelKey = ''
    
// Try to determine model key from model name (don't set modelGroup here).
// Match longer/more specific keys first so e.g. 'dragon-aku-8-duo' matches before 'dragon-8-duo'.
const modelKeys = Object.keys(MODEL_CODES).sort((a, b) => b.length - a.length)
for (const key of modelKeys) {
  const normalizedKey = key.replace(/[-_]/g, '-')
  if (model.name.toLowerCase().includes(normalizedKey)) {
    modelKey = key
    break
  }
}
  // Fallback: derive from model name if not found
  if (!modelGroup || !modelKey) {
      if (model.name.toLowerCase().includes('dragon')) {
          // Check for AKU models first
          if (model.type?.includes('aku') || model.name.toLowerCase().includes('aku')) {
            if (model.glassType === 'narozna') {
              modelGroup = 'dragon-narozne-aku'
        } else if (model.glassType === 'vis-a-vis') {
          // vis-a-vis AKU models belong to the vis-a-vis AKU group
          modelGroup = 'dragon-vis-a-vis-aku'
            } else {
              modelGroup = 'dragon-proste-aku'
            }
        } else if (model.name.toLowerCase().includes('portal')) {
          modelGroup = 'dragon-portal'
        } else if (model.name.toLowerCase().includes('2c')) {
          modelGroup = 'dragon-trojstronne'
        } else if (model.glassType === 'narozna') {
          modelGroup = 'dragon-narozne'
        } else if (model.glassType === 'vis-a-vis') {
          modelGroup = 'dragon-vis-a-vis'
        } else if (model.glassType === 'polokragla') {
          modelGroup = 'dragon-polokragle'
        } else {
          modelGroup = 'dragon-proste'
        }
      } else if (model.name.toLowerCase().includes('nemo')) {
        if (model.glassType === 'narozna') {
          modelGroup = 'nemo-narozne'
        } else if (model.glassType === 'vis-a-vis') {
          modelGroup = 'nemo-vis-a-vis'
        } else {
          modelGroup = 'nemo-proste'
        }
      }
      
      // Derive model key from name - for AKU models, include 'aku' in the key
      if (model.type?.includes('aku') || model.name.toLowerCase().includes('aku')) {
        modelKey = model.name.toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
      } else {
        modelKey = model.name.toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
      }
    }

    // If we found a modelKey but modelGroup wasn't determined, try to find the group
    // by looking up the key in the models data. This avoids misclassifying vis-a-vis
    // models as AKU when they live in the non-AKU group.
    if (modelKey && !modelGroup) {
      for (const [groupName, groupData] of Object.entries(modelsData)) {
        if ((groupData as any)[modelKey]) {
          modelGroup = groupName
          break
        }
      }
    }
    
    // Normalize group for AKU models to ensure correct product code group
    if (modelKey.includes('aku')) {
      if (modelGroup === 'dragon-proste') {
        modelGroup = 'dragon-proste-aku'
      } else if (modelGroup === 'dragon-narozne') {
        modelGroup = 'dragon-narozne-aku'
      } else if (modelGroup === 'dragon-vis-a-vis') {
        modelGroup = 'dragon-vis-a-vis-aku'
      }
    }

    // Generate using direct product code system
    const productCode = generateDirectProductCode(modelGroup, modelKey, config)
    
    // Fallback if generation fails
    return productCode || `${model.name.replace(/\s/g, '-').toUpperCase()}-${config.variant.toUpperCase()}`
  }

  // Get summary data for display
  const getSummaryData = () => {
    // Safety check for required data
    if (!model || !config || !pricing) {
      return {
        date: getCurrentDate(),
        model: 'Ładowanie...',
        power: '',
        variant: '',
        glassType: '',
        ceramic: '',
        handle: '',
        bezel: '',
        fusion: '',
        fusionCorner: '',
        akuPlus: 'none',
        basePrice: '0',
        glassSurcharge: '-',
        bezelPrice: '-',
        fusionPrice: '-',
        fusionCornerPrice: '-',
        totalPrice: '0',
        specs: { power: '', glassSize: '', width: '', height: '', depth: '', weight: '' }
      }
    }
    
    // Labels are now taken directly from model.labels

    // Check if LIFT variant is selected (for both standard and vis-a-vis models)
    const isLiftVariant = model?.glassType === 'vis-a-vis' 
      ? (config['front-variant'] === 'lift' || config['back-variant'] === 'lift')
      : config?.variant === 'lift';
    
    const modelNameWithLift = isLiftVariant 
      ? `${model?.name || 'Nieznany model'} LIFT`
      : (model?.name || 'Nieznany model');

    const summaryData = {
      date: getCurrentDate(),
      model: modelNameWithLift,
      power: model?.power || '',
      variant: '',
      glassType: '',
      ceramic: '',
      handle: '',
      bezel: '',
      fusion: '',
      fusionCorner: '',
      akuPlus: config?.akuPlus || 'none',
      basePrice: formatPrice(calculatePriceWithVAT(pricing?.basePrice || 0)) + ' zł' + (priceMode === 'netto' ? ' (netto)' : ''),
      glassSurcharge: '-',
      bezelPrice: (pricing?.breakdown?.bezelPrice || 0) > 0 ? `+${formatPrice(calculatePriceWithVAT(pricing.breakdown.bezelPrice))} zł` : '-',
      fusionPrice: (pricing?.breakdown?.fusionPrice || 0) > 0 ? `+${formatPrice(calculatePriceWithVAT(pricing.breakdown.fusionPrice))} zł` : '-',
      fusionCornerPrice: (pricing?.breakdown?.fusionCornerPrice || 0) > 0 ? `+${formatPrice(calculatePriceWithVAT(pricing.breakdown.fusionCornerPrice))} zł` : '-',
      totalPrice: formatPrice(calculatePriceWithVAT(pricing?.totalPrice || 0)) + ' zł' + (priceMode === 'netto' ? ' (netto)' : ''),
      specs: model?.specs || { 
        power: '', 
        glassSize: '', 
        weight: '',
        efficiency: '',
        energyClass: '',
        waterHeaterPower: '',
        waterCapacity: '',
        insertSize: '',
        fireplaceSize: '',
        maxLogLength: ''
      }
    }

    // Get glass price from glass options
    const glassOptions = generateGlassOptions(model, config)
    const selectedGlassOption = glassOptions.options.find(opt => opt.value === config?.glass)
    if (selectedGlassOption && selectedGlassOption.price && selectedGlassOption.price > 0) {
      summaryData.glassSurcharge = `+${formatPrice(calculatePriceWithVAT(selectedGlassOption.price))} zł`
    }

    if (model?.glassType === 'vis-a-vis') {
      const frontVariant = config?.['front-variant'] || 'standard'
      const backVariant = config?.['back-variant'] || 'standard'
      
      if (frontVariant === backVariant) {
        summaryData.variant = frontVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
      } else {
        const frontLabel = frontVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
        const backLabel = backVariant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
        summaryData.variant = `Przód: ${frontLabel}, Tył: ${backLabel}`
      }

      const frontHandle = config?.['front-handle'] || 'classic-black'
      const backHandle = config?.['back-handle'] || 'classic-black'
      
      if (frontHandle === backHandle) {
        summaryData.handle = model?.labels?.handle?.[frontHandle] || 'Klamka Classic - czarna'
      } else {
        const frontValue = model?.labels?.handle?.[frontHandle] || 'Klamka Classic - czarna'
        const backValue = model?.labels?.handle?.[backHandle] || 'Klamka Classic - czarna'
        summaryData.handle = `Przód: ${frontValue}, Tył: ${backValue}`
      }

      const frontBezel = config?.['front-bezel'] || 'none'
      const backBezel = config?.['back-bezel'] || 'none'
      
      if (frontBezel === backBezel) {
        summaryData.bezel = model?.labels?.bezel?.[frontBezel] || 'Bez blendy'
      } else {
        const frontValue = model?.labels?.bezel?.[frontBezel] || 'Bez blendy'
        const backValue = model?.labels?.bezel?.[backBezel] || 'Bez blendy'
        summaryData.bezel = `Przód: ${frontValue}, Tył: ${backValue}`
      }
    } else {
      summaryData.variant = config?.variant === 'standard' ? 'Otwieranie standardowe' : 'Otwieranie typu LIFT'
      summaryData.handle = model?.labels?.handle?.[config?.handle || 'classic-black'] || 'Klamka Classic - czarna'
      summaryData.bezel = model?.labels?.bezel?.[config?.bezel || 'none'] || 'Bez blendy'
    }

    summaryData.glassType = model?.labels?.glass?.[config?.glass || 'single'] || 'Szyba pojedyncza'
    summaryData.ceramic = model?.labels?.ceramic?.[config?.ceramic || 'refrabox-light'] || 'Ceramika jasna (REFRABOX Light)'

    // FUSION text
    const fusionParts = []
    if (config?.fusionBasic !== 'none') {
      fusionParts.push('Tak')
    }
    if (config?.fusionModules !== 'none') {
      fusionParts.push(`Dodatkowe MODUŁY: ${config?.fusionModulesCount || 0}`)
    }
    summaryData.fusion = fusionParts.length > 0 ? fusionParts.join(', ') : ''

    // FUSION CORNER text
    const fusionCornerParts = []
    if (config?.fusionCorner !== 'none') {
      fusionCornerParts.push('Tak')
    }
    if (config?.fusionCornerModules !== 'none') {
      fusionCornerParts.push(`Dodatkowe MODUŁY: ${config?.fusionCornerModulesCount || 0}`)
    }
    summaryData.fusionCorner = fusionCornerParts.length > 0 ? fusionCornerParts.join(', ') : ''

    return summaryData
  }

  const summaryData = getSummaryData()

  // Compute product code once so we can display and reuse it reliably
  const productCode = generateProductCode()

  // Check if we have minimum required data for rendering
  const hasRequiredData = model?.name && config && pricing && summaryData && isDataReady

  if (!hasRequiredData) {
    return (
      <div className="modal" id="summaryModal" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-content">
          <div className="modal-header">
            <h3>Ładowanie podsumowania...</h3>
            <button className="modal-close" onClick={onClose} aria-label="Zamknij">
              ✕
            </button>
          </div>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Przygotowywanie danych...</p>
          </div>
        </div>
      </div>
    )
  }

  const handleSendEmail = async () => {
    if (!user && !isAdminLoggedIn && !isClientLoggedIn) {
      alert('Musisz być zalogowany, aby wysłać email.')
      return
    }

    if (!model) {
      alert('Błąd: brak danych modelu')
      return
    }

    // Potwierdź wysyłkę
    const confirmed = confirm(`Czy chcesz wysłać konfigurację kominka ${model.name} na adres gktrener@gmail.com?`)
    if (!confirmed) return

    try {
      // Przygotuj dane do wysyłki
      let handleSideData: any = null
      
      if (model?.glassType === 'vis-a-vis') {
        // vis-a-vis models - send structured data
        const frontVariant = config['front-variant']
        const backVariant = config['back-variant']
        const frontHandleSide = config['front-handleSide']
        const backHandleSide = config['back-handleSide']
        handleSideData = {
          isVisAVis: true,
          front: {
            variant: frontVariant,
            side: frontVariant === 'standard' ? frontHandleSide : null,
            serviceSide: null
          },
          back: {
            variant: backVariant,
            side: backVariant === 'standard' ? backHandleSide : null,
            serviceSide: null
          }
        }
      } else {
        // Standard models
        let handleSideText = '-'
        
        const isCornerModel = model?.glassType === 'narozna';
        const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
        const hasAutoHandleSide = config.variant === 'standard' && isCornerModel && isDragonOrNemo && config.sideGlass;
        
        if (config.variant === 'standard' && (config.handleSide || hasAutoHandleSide)) {
          if (hasAutoHandleSide) {
            // For standard corner models with sideGlass, handle side is auto-determined
            const autoHandleSide = config.sideGlass === 'right' ? 'left' : 'right';
            handleSideText = autoHandleSide === 'left' ? 'Lewa' : 'Prawa';
          } else {
            // Regular standard models with manual handle side selection
            handleSideText = config.handleSide === 'left' ? 'Lewa' : 'Prawa';
          }
        }
        
        handleSideData = {
          isVisAVis: false,
          text: handleSideText,
          price: '-'
        }
      }
      
      // Przygotuj rozszerzone summaryData z dodatkowymi polami
      const extendedSummaryData = {
        ...summaryData,
        handleSideData: handleSideData,
        fusionLabel: (model?.glassType === 'narozna' || model?.glassType === 'corner') ? 'FUSION CORNER' : 'FUSION',
        akuBox: config.akuBox && config.akuBox !== 'none' ? 'Tak' : null,
        easy700: !isNemoModel && config.easy700 && config.easy700 !== 'none' ? 'Tak' : null,
        thermalModules: config.thermalModules && config.thermalModules !== 'none' 
          ? `${config.thermalModulesCount || 1} szt. (${(model?.labels?.thermalModules?.[config.thermalModules] || config.thermalModules).replace(/^Dodaj\s+/i, '')})`
          : null,
        thermalHangers: config.thermalHangers === 'yes' 
          ? (() => {
              const hangersData = getThermalHangersData(model);
              return hangersData ? `Komplet z uchwytami (Ciężar: ${hangersData.weight} kg)` : null;
            })()
          : null,
        controller: config.controller && config.controller !== 'none'
          ? (() => {
              const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller;
              const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb';
              
              if (requiresColor && config.controllerColor && config.controllerColor !== 'none') {
                const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK';
                return controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel;
              }
              return controllerLabel;
            })()
          : null
      }
      
      // Wywołaj Cloud Function do wysyłania emaila
      const sendEmail = httpsCallable(functions, 'sendConfigurationEmail')
      
      const result = await sendEmail({
        recipientEmail: 'gktrener@gmail.com',
        modelName: model.name,
        specs: model.specs,
        summaryData: extendedSummaryData,
        pricingBreakdown: pricing.breakdown,
        basePrice: pricing.basePrice,
        totalPrice: pricing.totalPrice,
        priceMode: priceMode,
        productCode: productCode,
        clientNotes: clientNotes,
        distributorInfo: distributorInfo
      })

      console.log('Email wysłany:', result.data)
      alert('Email został wysłany pomyślnie!')
    } catch (error: any) {
      console.error('Błąd wysyłania emaila:', error)
      alert(`Błąd wysyłania emaila: ${error.message || 'Nieznany błąd'}`)
    }
  }

  const handleCopyToClipboard = () => {
    const summaryText = generateEmailBody()
    copyToClipboard(summaryText)
  }

  const handleCopyProductCode = () => {
    copyToClipboardWithMessage(productCode, 'Kod produktu skopiowany do schowka!')
  }

  const copyToClipboardWithMessage = async (text: string, message: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      alert(message);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert(message);
    }
  }

  const handleDownloadPDF = () => {
    try {
      // Generuj definicję PDF używając pdfmake
      const docDefinition = generatePdfDefinition(
        summaryData,
        model,
        config,
        pricing,
        productCode,
        clientNotes,
        distributorInfo,
        logoDataUrl,
        priceMode
      )
      
      // Utwórz i pobierz PDF
      const filename = `konfiguracja-${productCode}.pdf`
      pdfMake.createPdf(docDefinition).download(filename)
      
    } catch (error) {
      console.error('PDF generation error:', error)
      alert('Wystąpił błąd podczas generowania PDF. Spróbuj ponownie.')
    }
  }

  const handleDownloadJPG = async () => {
    try {
      // Generuj TĘ SAMĄ definicję PDF co dla handleDownloadPDF
      const docDefinition = generatePdfDefinition(
        summaryData,
        model,
        config,
        pricing,
        productCode,
        clientNotes,
        distributorInfo,
        logoDataUrl,
        priceMode
      )
      
      // Utwórz PDF jako blob
      const pdfDocGenerator = pdfMake.createPdf(docDefinition)
      
      pdfDocGenerator.getBlob(async (pdfBlob: Blob) => {
        try {
          // Konwertuj blob na ArrayBuffer
          const arrayBuffer = await pdfBlob.arrayBuffer()
          
          // Załaduj PDF używając pdf.js
          const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer })
          const pdf = await loadingTask.promise
          
          // Iteruj przez wszystkie strony PDF
          const numPages = pdf.numPages
          
          for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            // Renderuj każdą stronę PDF
            const page = await pdf.getPage(pageNum)
            
            // Ustal skalę dla wysokiej jakości (300 DPI)
            const scale = 3.0
            const viewport = page.getViewport({ scale })
            
            // Utwórz canvas
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            
            if (!context) {
              throw new Error('Nie można utworzyć kontekstu canvas')
            }
            
            canvas.width = viewport.width
            canvas.height = viewport.height
            
            // Białe tło dla JPG
            context.fillStyle = '#ffffff'
            context.fillRect(0, 0, canvas.width, canvas.height)
            
            // Renderuj PDF na canvas
            const renderContext = {
              canvas: canvas,
              canvasContext: context,
              viewport: viewport
            }
            
            await page.render(renderContext).promise
            
            // Konwertuj canvas do JPG i pobierz
            await new Promise<void>((resolve) => {
              canvas.toBlob((blob: Blob | null) => {
                if (blob) {
                  const url = URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = url
                  // Jeśli jest tylko 1 strona, użyj prostej nazwy, inaczej dodaj numer strony
                  link.download = numPages === 1 
                    ? `konfiguracja-${productCode}.jpg`
                    : `konfiguracja-${productCode}_strona_${pageNum}.jpg`
                  document.body.appendChild(link)
                  link.click()
                  document.body.removeChild(link)
                  URL.revokeObjectURL(url)
                  resolve()
                } else {
                  alert('Błąd podczas tworzenia pliku JPG.')
                  resolve()
                }
              }, 'image/jpeg', 0.95)
            })
            
            // Krótka przerwa między pobieraniami dla przeglądarki
            if (pageNum < numPages) {
              await new Promise(resolve => setTimeout(resolve, 300))
            }
          }
          
        } catch (error) {
          console.error('PDF to JPG conversion error:', error)
          alert('Wystąpił błąd podczas konwersji PDF do JPG. Spróbuj ponownie.')
        }
      })
      
    } catch (error) {
      console.error('JPG generation error:', error)
      alert('Wystąpił błąd podczas generowania JPG. Spróbuj ponownie.')
    }
  }

  /* STARA WERSJA Z html2pdf - BACKUP
  const handleDownloadPDF = () => {
    const summaryElement = document.getElementById('summaryContent')
    
    // Check if html2pdf library is available
    if (!summaryElement) {
      alert('Błąd: Nie można znaleźć elementu do eksportu.')
      return
    }
    
    if (!(window as any).html2pdf) {
      alert('Biblioteka PDF nie jest dostępna. Spróbuj ponownie za chwilę lub odśwież stronę.')
      return
    }
    
    try {
      const opt = {
        margin: [10, 15, 10, 15],
        filename: `konfiguracja-${generateProductCode()}.pdf`,
        image: { type: 'jpeg', quality: 0.92 },
        html2canvas: { 
          scale: 2.5,
          useCORS: true,
          allowTaint: true,
          letterRendering: true,
          logging: false,
          dpi: 200,
          windowWidth: 1400,
          windowHeight: 900,
          height: null,
          width: null,
          scrollX: 0,
          scrollY: 0,
          backgroundColor: '#ffffff',
          removeContainer: true,
          imageTimeout: 12000
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true,
          precision: 12
        },
        pagebreak: { mode: 'avoid-all' }
      };
      
      (window as any).html2pdf().set(opt).from(summaryElement).save();
    } catch (error) {
      console.error('PDF generation error:', error)
      alert('Wystąpił błąd podczas generowania PDF. Spróbuj ponownie.')
    }
  }
  */

  const generateEmailBody = (): string => {
    let handleSideText = '-'
    
    if (model?.glassType === 'vis-a-vis') {
      // vis-a-vis models - handle front and back separately
      const frontVariant = config['front-variant']
      const backVariant = config['back-variant']
      const frontHandleSide = config['front-handleSide']
      const backHandleSide = config['back-handleSide']
      const handleSideLines = []
      
      // Show front first (standard or lift depending on variant)
      if (frontVariant === 'standard' && frontHandleSide) {
        handleSideLines.push(`- Strona klamki (Przód): ${frontHandleSide === 'left' ? 'Lewa' : 'Prawa'}`)
      }
      
      // Then show back (standard or lift depending on variant)
      if (backVariant === 'standard' && backHandleSide) {
        handleSideLines.push(`- Strona klamki (Tył): ${backHandleSide === 'left' ? 'Lewa' : 'Prawa'}`)
      }
      
      handleSideText = handleSideLines.length > 0 ? '\n' + handleSideLines.join('\n') : ''
    } else {
      // Standard models
      const isCornerModel = model?.glassType === 'narozna';
      const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
      const hasAutoHandleSide = config.variant === 'standard' && isCornerModel && isDragonOrNemo && config.sideGlass;
      
      if (config.variant === 'standard' && (config.handleSide || hasAutoHandleSide)) {
        if (hasAutoHandleSide) {
          // For standard corner models with sideGlass, handle side is auto-determined
          const autoHandleSide = config.sideGlass === 'right' ? 'left' : 'right';
          handleSideText = autoHandleSide === 'left' ? 'Lewa' : 'Prawa';
        } else {
          // Regular standard models with manual handle side selection
          handleSideText = config.handleSide === 'left' ? 'Lewa' : 'Prawa';
        }
      }
    }
    
    return `
KONFIGURATOR UNICO - ZESTAWIENIE KONFIGURACJI
Data: ${summaryData.date}

WYBRANY MODEL:
${summaryData.model} (${summaryData.power})

SZCZEGÓŁY KONFIGURACJI:
- Model bazowy: ${summaryData.model} ${summaryData.variant} - ${summaryData.basePrice}
- Konfiguracja szyby: ${summaryData.glassType}
${(config.variant === 'lift' || config.variant === 'standard') && model?.glassType === 'narozna' && (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) && (config.sideGlass === 'left' || config.sideGlass === 'right') ? `- Strona wkładu: ${config.sideGlass === 'left' ? 'Lewa' : 'Prawa'}` : ''}
- Ceramika ognioodporna: ${summaryData.ceramic}
- Klamka: ${summaryData.handle}${config.variant === 'lift' || model?.glassType === 'vis-a-vis' || (config.variant === 'standard' && model?.glassType === 'narozna' && (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) && config.sideGlass) ? handleSideText : '\n- Strona klamki: ' + handleSideText}
- Blenda: ${summaryData.bezel}${summaryData.bezelPrice !== '-' ? ' - ' + summaryData.bezelPrice : ''}
${isFusionAvailable(model) && summaryData.fusion ? `- FUSION: ${summaryData.fusion}${summaryData.fusionPrice !== '-' ? ' - ' + summaryData.fusionPrice : ''}` : ''}
${(() => { const variant = config.variant === 'lift' ? 'lift' : 'standard'; return isFusionCornerAvailable(model, variant) && summaryData.fusionCorner ? `- FUSION CORNER: ${summaryData.fusionCorner}${summaryData.fusionCornerPrice !== '-' ? ' - ' + summaryData.fusionCornerPrice : ''}` : ''; })()}
${isAkuPlusAvailable(model) && summaryData.akuPlus === 'aku-plus' ? `- AKU+: Tak${pricing.breakdown.akuPlusPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.akuPlusPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.nemoThermalProtection === 'with-protection' ? `- Zabezpieczenie termiczne chłodnicy: Tak${pricing.breakdown.nemoThermalProtectionPrice && pricing.breakdown.nemoThermalProtectionPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.nemoThermalProtectionPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.akuBox && config.akuBox !== 'none' ? `- AKU BOX: Tak${pricing.breakdown.akuBoxPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.akuBoxPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${!isNemoModel && config.easy700 && config.easy700 !== 'none' ? `- EASY 700: Tak${pricing.breakdown.easy700Price > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.easy700Price)) + ' zł' + getVATLabel() : ''}` : ''}
${config.cmaN && config.cmaN !== 'none' ? `- CMA N: Tak${pricing.breakdown.cmaNPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.cmaNPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.cmaS && config.cmaS !== 'none' ? `- CMA S: Tak${pricing.breakdown.cmaSPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.cmaSPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.thermalModules && config.thermalModules !== 'none' ? `- MODUŁY TERMOAKUMULACYJNE: ${config.thermalModulesCount || 1} szt. (${(model?.labels?.thermalModules?.[config.thermalModules] || config.thermalModules).replace(/^Dodaj\s+/i, '')})${pricing.breakdown.thermalModulesPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.thermalModulesPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.thermalHangers === 'yes' && (() => { const hangersData = getThermalHangersData(model); return hangersData ? `- ZAWIESIA TERMOAKUMULACYJNE: Komplet z uchwytami (Ciężar: ${hangersData.weight} kg)${pricing.breakdown.thermalHangersPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.thermalHangersPrice)) + ' zł' + getVATLabel() : ''}` : ''; })()}
${config.controller && config.controller !== 'none' ? `- STEROWNIK: ${(() => { const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller; const isNemo = model?.id?.startsWith('nemo-'); if (isNemo) { return getNemoControllerFullLabel(config.controller, config.controllerColor || 'none', config.controllerBuffor || false); } const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb'; if (requiresColor && config.controllerColor && config.controllerColor !== 'none') { const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK'; return controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel; } return controllerLabel; })()}${pricing.breakdown.controllerPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.controllerPrice)) + ' zł' + getVATLabel() : ''}` : ''}
${config.doorSensor === 'czujnik-drzwiczek' ? `- CZUJNIK DRZWICZEK: ${model?.labels?.controllers?.['czujnik-drzwiczek'] || 'Czujnik drzwiczek'}${pricing.breakdown.doorSensorPrice > 0 ? ' - +' + formatPrice(calculatePriceWithVAT(pricing.breakdown.doorSensorPrice)) + ' zł' + getVATLabel() : ''}` : ''}

CENA KOŃCOWA: ${summaryData.totalPrice} zł

DANE TECHNICZNE:
- Moc grzewcza: ${summaryData.specs.power}
${'efficiency' in summaryData.specs && summaryData.specs.efficiency ? `- Sprawność: -\n` : ''}
${'energyClass' in summaryData.specs && summaryData.specs.energyClass ? `- Klasa energetyczna: ${summaryData.specs.energyClass}\n` : ''}
${'waterHeaterPower' in summaryData.specs && summaryData.specs.waterHeaterPower ? `- Moc wymiennika wodnego: ${summaryData.specs.waterHeaterPower}\n` : ''}
${'waterCapacity' in summaryData.specs && summaryData.specs.waterCapacity ? `- Pojemność wymiennika: ${summaryData.specs.waterCapacity}\n` : ''}
- Wymiary fasady: ${summaryData.specs.glassSize}
${'insertSize' in summaryData.specs && summaryData.specs.insertSize ? `- Wymiary wkładu: ${summaryData.specs.insertSize}\n` : ''}
${'fireplaceSize' in summaryData.specs && summaryData.specs.fireplaceSize ? `- Wymiary paleniska: ${summaryData.specs.fireplaceSize}\n` : ''}
${'maxLogLength' in summaryData.specs && summaryData.specs.maxLogLength ? `- Maksymalna długość polan: ${summaryData.specs.maxLogLength}\n` : ''}
- Ciężar: ${summaryData.specs.weight}
- Kod produktu: ${generateProductCode()}

${clientNotes ? `UWAGI:\n${clientNotes}\n` : ''}
UWAGI:
• Cena nie zawiera transportu i montażu
• *Dostępność opcji kolorystycznych do ustalenia z działem sprzedaży  
• Proszę o potwierdzenie dostępności i terminu dostawy
• W razie pytań proszę o kontakt

Wiadomość wygenerowana przez konfigurator UNICO
    `.trim()
  }

  return (
    <div className="modal" id="summaryModal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content" style={{ maxWidth: '900px', width: '90%' }}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px' }}>
          <button className="modal-close" onClick={onClose} aria-label="Zamknij">
            ✕
          </button>
        </div>

        <style>{`
          @media screen {
            .client-notes-input {
              display: block !important;
            }
            .pdf-page {
              page-break-after: unset !important;
              min-height: unset !important;
            }
            .pdf-only {
              display: none !important;
            }
            
            .pdf-preview-only {
              display: none !important;
            }
            
            .modal-hidden {
              display: none !important;
            }
            
            @media print {
              .modal-hidden {
                display: block !important;
              }
            }

            .copy-code-btn {
              background: var(--accent);
              color: white;
              border: none;
              padding: 4px 8px;
              border-radius: 3px;
              fontSize: 12px;
              cursor: pointer;
              margin-left: 10px;
              display: flex;
              align-items: center;
              gap: 4px;
              transition: background-color 0.2s ease;
            }
            .copy-code-btn:hover {
              background: #b8965a;
            }
          }
          
          @media print {
            .client-notes-input {
              display: none !important;
            }
            .copy-code-btn {
              display: none !important;
            }
            .no-print {
              display: none !important;
            }
            .screen-only {
              display: none !important;
            }

          }
          
          .no-print {
            display: inline-flex;
          }
          
          #summaryContent .no-print {
            display: none !important;
          }
          
          /* Ukryj logo tylko w modalu, ale nie w PDF */
          .modal .pdf-logo {
            visibility: hidden !important;
          }
          
          @page {
            margin: 0.5in;
            size: A4;
          }
        `}</style>

        <div className="summary-content" id="summaryContent">
          {/* Jedna strona PDF z logo w prawym górnym rogu */}
          <div className="pdf-page" style={{ position: 'relative', minHeight: '90vh' }}>
            {/* Logo w prawym górnym rogu */}
            <div className="pdf-logo" style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              zIndex: 10,
              backgroundColor: '#2B3E50',
              padding: '15px 25px',
              borderRadius: '0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src="images/unico-logo.png" 
                alt="UNICO logo" 
                style={{ 
                  height: '40px',
                  width: 'auto',
                  display: 'block',
                  maxWidth: '120px'
                }}
              />
            </div>

            {/* Tytuł dokumentu */}
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ 
                margin: '0 0 10px 0', 
                color: '#C4A574', 
                fontSize: '20px',
                fontWeight: 'normal',
                letterSpacing: '1px'
              }}>KONFIGURACJA PRODUKTU UNICO</h2>
            </div>
            
            {/* Dane podstawowe */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Model:</strong> {summaryData.model}</p>
              <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Moc:</strong> {summaryData.power}</p>
              <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Wymiary fasady:</strong> {summaryData.specs.glassSize}</p>
              {'efficiency' in summaryData.specs && summaryData.specs.efficiency && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Sprawność:</strong> -</p>}
              {'energyClass' in summaryData.specs && summaryData.specs.energyClass && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Klasa energetyczna:</strong> {summaryData.specs.energyClass}</p>}
              {'waterHeaterPower' in summaryData.specs && summaryData.specs.waterHeaterPower && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Moc wymiennika:</strong> {summaryData.specs.waterHeaterPower}</p>}
              {'waterCapacity' in summaryData.specs && summaryData.specs.waterCapacity && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Pojemność wymiennika:</strong> {summaryData.specs.waterCapacity}</p>}
              {'insertSize' in summaryData.specs && summaryData.specs.insertSize && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Wymiary wkładu:</strong> {summaryData.specs.insertSize}</p>}
              {'fireplaceSize' in summaryData.specs && summaryData.specs.fireplaceSize && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Wymiary paleniska:</strong> {summaryData.specs.fireplaceSize}</p>}
              {'maxLogLength' in summaryData.specs && summaryData.specs.maxLogLength && <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Maks. długość polan:</strong> {summaryData.specs.maxLogLength}</p>}
              <p style={{ fontSize: '12px', margin: '3px 0' }}><strong>Waga:</strong> {summaryData.specs.weight}</p>
            </div>
            
            <h4 style={{ fontSize: '12px', color: '#C4A574', marginBottom: '10px', marginTop: '25px', fontWeight: 'bold' }}>Szczegóły konfiguracji:</h4>
            <table className="summary-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '12px', borderTop: '0.5px solid #ddd', borderBottom: '0.5px solid #ddd' }}>
              <thead>
                <tr style={{ backgroundColor: '#E5D4B8' }}>
                  <th style={{ padding: '8px', fontWeight: 'bold', textAlign: 'left', color: '#000 !important', borderBottom: '0.5px solid #ddd' }}>Pozycja</th>
                  <th style={{ padding: '8px', fontWeight: 'bold', textAlign: 'left', color: '#000 !important', borderBottom: '0.5px solid #ddd' }}>Opis</th>
                  <th style={{ padding: '8px', fontWeight: 'bold', textAlign: 'right', color: '#000 !important', borderBottom: '0.5px solid #ddd' }}>Cena netto</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd', fontWeight: '500' }}>Model</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.model}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd', fontWeight: '500' }}>
                    {(() => {
                      // For corner models, show cheapest glass price; for others, show base price minus glass surcharge
                      if (model?.glassType === 'narozna') {
                        const variant = config?.variant === 'lift' ? 'lift' : 'standard';
                        const variantPrices = model.basePrices?.[variant] || {};
                        const cheapestPrice = Math.min(...Object.values(variantPrices).filter(price => typeof price === 'number'));
                        return formatPrice(cheapestPrice);
                      } else {
                        return formatPrice(pricing.basePrice - pricing.breakdown.glassPrice);
                      }
                    })()} zł
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Wariant otwierania</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.variant}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Szyba</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.glassType}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                    {pricing.breakdown.glassPrice > 0 ? `+${formatPrice(pricing.breakdown.glassPrice)} zł` : '-'}
                  </td>
                </tr>
                {/* Side selection for corner Dragon/Nemo models (both standard and LIFT) */}
                {(config.variant === 'lift' || config.variant === 'standard') && model?.glassType === 'narozna' && 
                 (model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo')) &&
                 (config.sideGlass === 'left' || config.sideGlass === 'right') && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Strona wkładu</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {config.sideGlass === 'left' ? 'Lewa' : 'Prawa'}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Ceramika (wyłożenie)</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.ceramic}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Klamka</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.handle}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                </tr>
                {(() => {
                  if (model?.glassType === 'vis-a-vis') {
                    // Vis-a-vis models - show front first (standard or lift), then back
                    const frontVariant = config['front-variant'];
                    const backVariant = config['back-variant'];
                    const frontHandleSide = config['front-handleSide'];
                    const backHandleSide = config['back-handleSide'];
                    
                    const rows = [];
                    
                    // Show front first (standard or lift depending on variant)
                    if (frontVariant === 'standard' && frontHandleSide) {
                      rows.push(
                        <tr key="front-handle">
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Strona klamki (Przód)</td>
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{frontHandleSide === 'left' ? 'Lewa' : 'Prawa'}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                        </tr>
                      );

                    }
                    
                    // Then show back (standard or lift depending on variant)
                    if (backVariant === 'standard' && backHandleSide) {
                      rows.push(
                        <tr key="back-handle">
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Strona klamki (Tył)</td>
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{backHandleSide === 'left' ? 'Lewa' : 'Prawa'}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                        </tr>
                      );

                    }
                    
                    return rows.length > 0 ? rows : null;
                  } else {
                    // Standard models
                    const isCornerModel = model?.glassType === 'narozna';
                    const isDragonOrNemo = model?.name?.toLowerCase().includes('dragon') || model?.name?.toLowerCase().includes('nemo');
                    const hasAutoHandleSide = config.variant === 'standard' && isCornerModel && isDragonOrNemo && config.sideGlass;
                    
                    const showHandleSide = 
                      (config.variant === 'standard' && (config.handleSide || hasAutoHandleSide));
                    
                    if (!showHandleSide) return null;
                    
                    let label = 'Strona klamki';
                    let value = '-';
                    
                    if (config.variant === 'standard' && (config.handleSide || hasAutoHandleSide)) {
                      if (hasAutoHandleSide) {
                        // For standard corner models with sideGlass, handle side is auto-determined
                        const autoHandleSide = config.sideGlass === 'right' ? 'left' : 'right';
                        value = autoHandleSide === 'left' ? 'Lewa' : 'Prawa';
                      } else {
                        // Regular standard models with manual handle side selection
                        value = config.handleSide === 'left' ? 'Lewa' : 'Prawa';
                      }
                    }
                    
                    return (
                      <tr>
                        <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{label}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{value}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>-</td>
                      </tr>
                    );
                  }
                })()}
                <tr>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Blenda</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.bezel}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                    {pricing.breakdown.bezelPrice > 0 ? `+${formatPrice(pricing.breakdown.bezelPrice)} zł` : '-'}
                  </td>
                </tr>
                {isFusionAvailable(model) && summaryData.fusion && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      Zabudowa FUSION
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.fusion}</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.fusionPrice > 0 ? `+${formatPrice(pricing.breakdown.fusionPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {(() => { 
                  const variant = config.variant === 'lift' ? 'lift' : 'standard'; 
                  return isFusionCornerAvailable(model, variant) && summaryData.fusionCorner && (
                    <tr>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                        Zabudowa FUSION CORNER
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>{summaryData.fusionCorner}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                        {pricing.breakdown.fusionCornerPrice > 0 ? `+${formatPrice(pricing.breakdown.fusionCornerPrice)} zł` : '-'}
                      </td>
                    </tr>
                  ); 
                })()}
                {isAkuPlusAvailable(model) && summaryData.akuPlus === 'aku-plus' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>AKU+</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.akuPlusPrice > 0 ? `+${formatPrice(pricing.breakdown.akuPlusPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.nemoThermalProtection === 'with-protection' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Zabezpieczenie termiczne chłodnicy</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.nemoThermalProtectionPrice && pricing.breakdown.nemoThermalProtectionPrice > 0 ? `+${formatPrice(pricing.breakdown.nemoThermalProtectionPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.akuBox && config.akuBox !== 'none' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Wymiennik akumulacyjny AKUBOX</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.akuBoxPrice > 0 ? `+${formatPrice(pricing.breakdown.akuBoxPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {!isNemoModel && config.easy700 && config.easy700 !== 'none' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Wymiennik stalowo-szamotowy Easy 700</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.easy700Price > 0 ? `+${formatPrice(pricing.breakdown.easy700Price)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.cmaN && config.cmaN !== 'none' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Moduł akumulacyjny CMA N (333 kg, 112x56x35 mm)</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.cmaNPrice > 0 ? `+${formatPrice(pricing.breakdown.cmaNPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.cmaS && config.cmaS !== 'none' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Moduł akumulacyjny CMA S (338 kg, 119x56x28 mm)</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Tak</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.cmaSPrice > 0 ? `+${formatPrice(pricing.breakdown.cmaSPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.thermalModules && config.thermalModules !== 'none' && (
                  <tr className="thermal-modules-row">
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>MODUŁY TERMOAKUMULACYJNE</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {(() => {
                        const count = config.thermalModulesCount || 1;
                        const fullLabel = (model?.labels?.thermalModules?.[config.thermalModules] || config.thermalModules).replace(/^Dodaj\s+/i, '');
                        
                        // Always show full description now
                        return `${count} szt. (${fullLabel})`;
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.thermalModulesPrice > 0 ? `+${formatPrice(pricing.breakdown.thermalModulesPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.thermalHangers && config.thermalHangers === 'yes' && (() => {
                  const hangersData = getThermalHangersData(model);
                  return hangersData ? (
                    <tr>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>ZAWIESIA TERMOAKUMULACYJNE</td>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                        Komplet z uchwytami (Ciężar: {hangersData.weight} kg)
                      </td>
                      <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                        {pricing.breakdown.thermalHangersPrice > 0 ? `+${formatPrice(pricing.breakdown.thermalHangersPrice)} zł` : '-'}
                      </td>
                    </tr>
                  ) : null;
                })()}
                {config.controller && config.controller !== 'none' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>STEROWNIK</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {(() => {
                        const controllerLabel = model?.labels?.controllers?.[config.controller] || config.controller;
                        const requiresColor = config.controller === 'rt-08-om-grafik-wb' || config.controller === 'rt-08-om-grafik-duo-integra-wb';
                        if (requiresColor && config.controllerColor && config.controllerColor !== 'none') {
                          const colorLabel = config.controllerColor === 'white' ? 'WHITE' : 'BLACK';
                          return controllerLabel.replace(' WHITE / BLACK', '') + ' ' + colorLabel;
                        }
                        return controllerLabel;
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.controllerPrice > 0 ? `+${formatPrice(pricing.breakdown.controllerPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
                {config.doorSensor === 'czujnik-drzwiczek' && (
                  <tr>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>Czujnik drzwiczek</td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      Tak
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '0.5px solid #ddd' }}>
                      {pricing.breakdown.doorSensorPrice > 0 ? `+${formatPrice(pricing.breakdown.doorSensorPrice)} zł` : '-'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Tabela RAZEM z rozbiciem VAT jeśli dotyczy */}
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              marginTop: '20px',
              marginBottom: '20px'
            }}>
              <tbody>
                {(priceMode === 'vat8' || priceMode === 'vat23') ? (
                  <>
                    <tr style={{ border: '0.5px solid #E5D4B8' }}>
                      <td style={{ padding: '6px 8px', fontSize: '12px', width: '80%' }}>Cena netto</td>
                      <td style={{ padding: '6px 8px', fontSize: '12px', width: '20%', textAlign: 'right' }}>
                        {formatPrice(pricing.totalPrice)} zł
                      </td>
                    </tr>
                    <tr style={{ border: '0.5px solid #E5D4B8' }}>
                      <td style={{ padding: '6px 8px', fontSize: '12px' }}>
                        VAT {priceMode === 'vat8' ? '8%' : '23%'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: '12px', textAlign: 'right' }}>
                        {formatPrice(Math.round(pricing.totalPrice * (priceMode === 'vat8' ? 0.08 : 0.23)))} zł
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: '#E5D4B8' }}>
                      <td style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold' }}>RAZEM</td>
                      <td style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold', textAlign: 'right' }}>
                        {formatPrice(pricing.totalPrice + Math.round(pricing.totalPrice * (priceMode === 'vat8' ? 0.08 : 0.23)))} zł
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr style={{ backgroundColor: '#E5D4B8' }}>
                    <td style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold' }}>RAZEM</td>
                    <td style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold' }}></td>
                    <td style={{ padding: '8px', fontSize: '12px', fontWeight: 'bold', textAlign: 'right', whiteSpace: 'nowrap' }} className="razem-price-cell">
                      {summaryData.totalPrice}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Kod konfiguracji */}
            <div style={{ marginBottom: '30px' }}>
              <p style={{ fontSize: '12px', margin: '3px 0' }}>
                <strong>Kod konfiguracji:</strong> <span style={{ color: '#C4A574' }}>{productCode}</span>
                <button 
                  onClick={handleCopyProductCode}
                  className="copy-code-btn no-print"
                  style={{ marginLeft: '10px', fontSize: '12px' }}
                  title="Skopiuj kod produktu"
                >
                  📋 Skopiuj
                </button>
              </p>
            </div>
            
            {/* Uwagi i informacje dodatkowe */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '12px', color: '#C4A574', marginBottom: '8px', fontWeight: 'bold' }}>Uwagi i informacje dodatkowe:</h4>
              <ul style={{ margin: '0', padding: '0 0 0 20px', lineHeight: '1.6', fontSize: '12px' }}>
                <li>Cena nie zawiera transportu i montażu</li>
                <li>Dostępność i termin wysyłki do ustalenia z działem sprzedaży</li>
              </ul>
            </div>
            
            {/* Uwagi - edytowalne w modalu, podgląd w PDF */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '12px', color: '#C4A574', marginBottom: '8px', fontWeight: 'bold' }}>Uwagi:</h4>
              <textarea 
                className="screen-only"
                placeholder="Dodaj swoje uwagi lub dodatkowe informacje..." 
                style={{ 
                  width: '100%', 
                  minHeight: '100px',
                  padding: '12px 16px', 
                  border: '0.5px solid #E5D4B8', 
                  borderRadius: '0',
                  fontSize: '12px',
                  backgroundColor: '#ffffff',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease',
                  outline: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  lineHeight: '1.5'
                }}
                onFocus={(e) => e.target.style.borderColor = '#C4A574'}
                onBlur={(e) => e.target.style.borderColor = '#E5D4B8'}
                value={clientNotes}
                onChange={(e) => setClientNotes(e.target.value)}
              />
              <div className="pdf-only" style={{ fontSize: '12px', lineHeight: '1.6' }}>
                {clientNotes || ''}
              </div>
            </div>
            
            {/* Dystrybutor - edytowalny w modalu, podgląd w PDF */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ fontSize: '12px', color: '#C4A574', marginBottom: '8px', fontWeight: 'bold' }}>Dystrybutor:</h4>
              <input 
                className="screen-only"
                type="text"
                placeholder="Wpisz dane dystrybutora..."
                style={{ 
                  width: '100%',
                  padding: '12px 16px', 
                  border: '0.5px solid #E5D4B8', 
                  borderRadius: '0',
                  fontSize: '12px',
                  backgroundColor: '#ffffff',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.2s ease',
                  outline: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                }}
                onFocus={(e) => e.target.style.borderColor = '#C4A574'}
                onBlur={(e) => e.target.style.borderColor = '#E5D4B8'}
                value={distributorInfo}
                onChange={(e) => setDistributorInfo(e.target.value)}
              />
              <div className="pdf-only" style={{ fontSize: '12px' }}>
                <p style={{ margin: '0' }}><strong>Dystrybutor:</strong> {distributorInfo || ''}</p>
              </div>
            </div>
            

            

            
            {/* Informacja o wygenerowaniu dokumentu na samym dole - tylko dla PDF */}
            <div className="pdf-only" style={{ 
              marginTop: '20px', 
              paddingTop: '15px',
              borderTop: '1px solid #ddd',
              fontSize: '12px',
              color: '#666',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0' }}>Dokument wygenerowany automatycznie – {summaryData.date}</p>
            </div>
          </div>
          

        </div>



        <div className="modal-buttons" style={(user || isAdminLoggedIn || isClientLoggedIn) ? {} : { justifyContent: 'flex-end' }}>
          {/* Przycisk Kopiuj - tymczasowo ukryty, ale pozostawiony w kodzie */}
          {false && (
            <button className="btn btn-secondary" onClick={handleCopyToClipboard}>
              <span className="btn-icon">📋</span>
              Kopiuj
            </button>
          )}
          
          {/* Przyciski tylko dla zalogowanych użytkowników (klient lub admin) */}
          {(user || isAdminLoggedIn || isClientLoggedIn) && (
            <>
              <button className="btn btn-primary" onClick={handleSendEmail}>
                <span className="btn-icon">📧</span>
                Wyślij email
              </button>
              <button 
                className="btn btn-save" 
                onClick={() => setShowSaveDialog(true)}
                title="Zapisz konfigurację"
              >
                <span className="btn-icon">💾</span>
                Zapisz
              </button>
            </>
          )}
          
          {/* Przyciski PDF i JPG - dostępne dla wszystkich, zawsze po prawej */}
          <button 
            className="btn btn-pdf" 
            onClick={handleDownloadPDF}
            disabled={!isPdfLibraryReady}
            title={isPdfLibraryReady ? "Pobierz jako PDF" : "Ładowanie biblioteki PDF..."}
          >
            {isPdfLibraryReady ? 'Pobierz PDF' : 'Ładowanie...'}
          </button>
          
          <button 
            className="btn btn-jpg" 
            onClick={handleDownloadJPG}
            disabled={!isPdfLibraryReady}
            title={isPdfLibraryReady ? "Pobierz jako JPG" : "Ładowanie biblioteki..."}
          >
            {isPdfLibraryReady ? 'Pobierz JPG' : 'Ładowanie...'}
          </button>
        </div>
      </div>

      {/* Save Configuration Dialog */}
      {showSaveDialog && (
        <div className="modal-overlay" onClick={() => setShowSaveDialog(false)}>
          <div className="modal save-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Zapisz konfigurację</h2>
              <button className="modal-close" onClick={() => setShowSaveDialog(false)} aria-label="Zamknij">
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label htmlFor="configName">Nazwa konfiguracji:</label>
                <input
                  id="configName"
                  type="text"
                  value={configName}
                  onChange={(e) => setConfigName(e.target.value)}
                  placeholder="Wprowadź nazwę konfiguracji"
                  maxLength={100}
                  autoFocus
                />
              </div>
              <div className="modal-buttons">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowSaveDialog(false)}
                >
                  Anuluj
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveConfiguration}
                  disabled={saving || !configName.trim()}
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SummaryModal
